// src/pages/AdminExtensionLogs.tsx
// Eklenti tam-eylem loglarını görüntüleme ekranı. Bir siparişin (FlowId) tüm
// adımlarını baştan sona izlemek için tasarlandı — bug'ları tahminle değil
// gerçek adımlarla teşhis etmek için.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, X, Search, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import type { ExtensionLogRow } from '../services/api';

const LEVELS = ['', 'debug', 'info', 'warn', 'error'];
const levelCls = (lvl?: string | null) => {
  switch ((lvl || '').toLowerCase()) {
    case 'error': return 'bg-rose-100 text-rose-700 border-rose-200';
    case 'warn': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'info': return 'bg-sky-100 text-sky-700 border-sky-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function pretty(json?: string | null): string {
  if (!json) return '';
  try { return JSON.stringify(JSON.parse(json), null, 2); } catch { return json; }
}

export default function AdminExtensionLogs() {
  const [items, setItems] = useState<ExtensionLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [software, setSoftware] = useState('');
  const [level, setLevel] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [facets, setFacets] = useState<{ emails: string[]; softwares: string[]; actions: string[] }>({ emails: [], softwares: [], actions: [] });
  const [expanded, setExpanded] = useState<number | null>(null);

  // FlowId zaman çizelgesi paneli
  const [flowId, setFlowId] = useState<string | null>(null);
  const [flowItems, setFlowItems] = useState<ExtensionLogRow[]>([]);
  const [flowLoading, setFlowLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getExtensionLogs({
        email: email || undefined, software: software || undefined,
        level: level || undefined, action: action || undefined,
        q: q || undefined, page, pageSize,
      });
      setItems(res.items || []); setTotal(res.total || 0);
    } catch (e: any) {
      if (String(e?.message || '').includes('503')) toast.error('Loglama kapalı (LogConnection yok)');
      else toast.error(e?.message || 'Loglar yüklenemedi');
    } finally { setLoading(false); }
  }, [email, software, level, action, q, page, pageSize]);

  useEffect(() => { load(); }, [page, software, level, action]); // eslint-disable-line
  useEffect(() => { apiService.getExtensionLogFacets(7).then(f => setFacets({ emails: f.emails || [], softwares: f.softwares || [], actions: f.actions || [] })).catch(() => {}); }, []);

  // metin filtreleri debounce
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 450); return () => clearTimeout(t); }, [email, q]); // eslint-disable-line

  // otomatik yenileme
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => { if (page === 1) load(); }, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, page, load]);

  const openFlow = async (fid: string) => {
    setFlowId(fid); setFlowItems([]); setFlowLoading(true);
    try {
      const res = await apiService.getExtensionLogFlow(fid);
      setFlowItems(res.items || []);
    } catch (e: any) { toast.error(e?.message || 'Akış yüklenemedi'); }
    finally { setFlowLoading(false); }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <div className="space-y-4">
      {/* Filtre çubuğu */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3">
        <div className="relative min-w-[200px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="E-posta ara…" className="w-full rounded-lg border border-gray-200 py-2 pl-8 pr-2 text-sm outline-none focus:border-indigo-400" />
        </div>
        <select value={software} onChange={e => { setPage(1); setSoftware(e.target.value); }} className="rounded-lg border border-gray-200 px-2 py-2 text-sm outline-none focus:border-indigo-400">
          <option value="">Tüm yazılımlar</option>
          {facets.softwares.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={level} onChange={e => { setPage(1); setLevel(e.target.value); }} className="rounded-lg border border-gray-200 px-2 py-2 text-sm outline-none focus:border-indigo-400">
          {LEVELS.map(l => <option key={l || 'all'} value={l}>{l ? l.toUpperCase() : 'Tüm seviyeler'}</option>)}
        </select>
        <select value={action} onChange={e => { setPage(1); setAction(e.target.value); }} className="max-w-[180px] rounded-lg border border-gray-200 px-2 py-2 text-sm outline-none focus:border-indigo-400">
          <option value="">Tüm aksiyonlar</option>
          {facets.actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Metin/JSON içinde ara…" className="min-w-[160px] flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="h-4 w-4 rounded" /> Otomatik yenile
        </label>
        <button onClick={() => load()} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Yenile
        </button>
      </div>

      {/* Tablo */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2.5 font-medium">Zaman</th>
                <th className="px-3 py-2.5 font-medium">Seviye</th>
                <th className="px-3 py-2.5 font-medium">Yazılım</th>
                <th className="px-3 py-2.5 font-medium">Aksiyon</th>
                <th className="px-3 py-2.5 font-medium">Mesaj</th>
                <th className="px-3 py-2.5 font-medium">Kullanıcı</th>
                <th className="px-3 py-2.5 font-medium">Akış</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-400">Yükleniyor…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-400">Log yok. (Eklenti 5.7+ kurulu ve bir sipariş akışı çalıştıysa buraya düşer.)</td></tr>
              ) : rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-gray-600">{fmt(r.createdAtUtc)}</td>
                    <td className="px-3 py-2"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${levelCls(r.level)}`}>{(r.level || 'info').toUpperCase()}</span></td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.software || '—'}</td>
                    <td className="px-3 py-2"><span className="font-mono text-xs text-indigo-700">{r.action || '—'}</span></td>
                    <td className="max-w-[320px] truncate px-3 py-2 text-gray-700" title={r.message || ''}>{r.message || (r.dataJson ? <span className="text-gray-400">{'{…}'}</span> : '—')}</td>
                    <td className="max-w-[160px] truncate px-3 py-2 text-xs text-gray-500" title={r.userEmail || ''}>{r.userEmail || '—'}</td>
                    <td className="px-3 py-2">
                      {r.flowId ? (
                        <button onClick={(e) => { e.stopPropagation(); openFlow(r.flowId!); }} title="Bu siparişin tüm adımları" className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
                          <Layers size={11} /> akış
                        </button>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="bg-gray-50/60">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="grid gap-3 md:grid-cols-[1fr_2fr]">
                          <div className="space-y-1 text-xs text-gray-600">
                            <div><span className="text-gray-400">URL:</span> <span className="break-all">{r.url || '—'}</span></div>
                            <div><span className="text-gray-400">Sürüm:</span> {r.extVersion || '—'} · <span className="text-gray-400">Tab:</span> {r.tabId || '—'}</div>
                            <div><span className="text-gray-400">FlowId:</span> <span className="break-all font-mono">{r.flowId || '—'}</span></div>
                            <div><span className="text-gray-400">İstemci saati:</span> {fmt(r.clientTsUtc)}</div>
                          </div>
                          <pre className="max-h-72 overflow-auto rounded-lg bg-gray-900 p-3 text-[11px] leading-relaxed text-emerald-200">{pretty(r.dataJson) || '(detay yok)'}</pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {total > pageSize && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2.5 text-sm text-gray-500">
            <span>{total} kayıt · sayfa {page}/{totalPages}</span>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-100"><ChevronLeft size={16} /></button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-100"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* FlowId zaman çizelgesi paneli */}
      {flowId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setFlowId(null)} />
          <div className="relative flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-800">Sipariş Akışı — adım adım</h2>
                <p className="break-all font-mono text-[11px] text-gray-400">{flowId}</p>
              </div>
              <button onClick={() => setFlowId(null)} className="grid h-8 w-8 place-items-center rounded-lg text-gray-500 hover:bg-gray-100"><X size={18} /></button>
            </div>
            {flowLoading ? (
              <div className="p-10 text-center text-gray-400">Yükleniyor…</div>
            ) : flowItems.length === 0 ? (
              <div className="p-10 text-center text-gray-400">Bu akışta olay yok.</div>
            ) : (
              <ol className="flex flex-col gap-0 p-4">
                {flowItems.map((it, i) => (
                  <li key={it.id} className="relative flex gap-3 pb-4">
                    <div className="flex flex-col items-center">
                      <span className={`grid h-6 w-6 flex-none place-items-center rounded-full border text-[10px] font-bold ${levelCls(it.level)}`}>{i + 1}</span>
                      {i < flowItems.length - 1 && <span className="w-px flex-1 bg-gray-200" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-indigo-700">{it.action || '—'}</span>
                        <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${levelCls(it.level)}`}>{(it.level || 'info').toUpperCase()}</span>
                        <span className="text-[11px] tabular-nums text-gray-400">{fmt(it.clientTsUtc || it.createdAtUtc)}</span>
                      </div>
                      {it.message && <div className="mt-0.5 text-sm text-gray-700">{it.message}</div>}
                      {it.dataJson && <pre className="mt-1 max-h-48 overflow-auto rounded-md bg-gray-900 p-2 text-[10px] leading-relaxed text-emerald-200">{pretty(it.dataJson)}</pre>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
